import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { storage } from 'utils/usePersistState';
import { BsHeart, BsHeartFill } from 'react-icons/bs';
import useOnClickOutside from 'utils/useOnClickOutside';
import { usePartner } from 'modules/auth/hooks/usePartner';
import { useIsFavorite } from './Favorite';

const GridWrapper = React.forwardRef(({ className, children }, ref) => (
  <div
    ref={ref}
    className={cn('relative h-24 bg-gray-200 rounded-xl', className)}
  >
    {children}
  </div>
));

GridWrapper.defaultProps = {
  className: '',
  children: '',
};

GridWrapper.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.number,
    PropTypes.string,
  ]),
};

const ListWrapper = React.forwardRef(({ className, children }, ref) => (
  <div
    ref={ref}
    className={cn('relative h-16 bg-gray-200 rounded-xl', className)}
  >
    {children}
  </div>
));

ListWrapper.defaultProps = {
  className: '',
  children: '',
};

ListWrapper.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.number,
    PropTypes.string,
  ]),
};

const openGame = (game_id, url, partner_id) => {
  const session = storage.get('user-session');
  window.open(
    `${url}?auth=${session.token}&uuid=${game_id}&lobby=${window.location.origin}&partner_id=${partner_id}`,
    '_self'
  );
};

const GridItem = ({ data, onSelectGame }) => {
  const ref = React.useRef();
  const [focus, setFocus] = React.useState(false);
  useOnClickOutside(ref, () => setFocus(false));
  const [partner] = usePartner();
  const handleSelect = () => {
    if (focus && data.is_playable && !data?.is_undermaintenance) {
      onSelectGame(data);
      openGame(data.id, data.url, partner.id);
    }
    if (
      focus &&
      !data.is_playable &&
      (data.code === 'gb-hideandseek' ||
        data.code === 'gb-pitikbulag' ||
        data.code === 'gb-sipa') &&
      !data?.is_undermaintenance
    ) {
      window.location.href = `/coming-soon?code=${data?.code}`;
    }
    setFocus(true);
  };
  const [isFavorite, { setFavorite, removeFavorite }] = useIsFavorite(data.id);
  const handleFavorite = () => {
    if (isFavorite) setFavorite(data.id);
    if (!isFavorite) removeFavorite(data.id);
  };

  return (
    <GridWrapper ref={ref}>
      <button
        className={cn(
          'absolute inset-0 h-full w-full focus:outline-none z-50 block rounded-lg',
          'focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
        )}
        type="button"
        onClick={handleSelect}
      >
        <span className="sr-only">Select {data.name}</span>
      </button>
      <div className="relative h-full w-full">
        {!data.is_playable ? (
          <div className="flex justify-center">
            <div
              className={cn(
                data.is_undermaintenance ? 'bg-red-600' : 'bg-black/60',
                'mt-2 text-center font-bold uppercase absolute text-white px-3 py-1 rounded-lg z-10 text-xs'
              )}
            >
              {data.is_undermaintenance ? 'Under Maintenance' : 'Coming Soon'}
            </div>
          </div>
        ) : null}
        <img
          className="absolute h-full w-full z-0 object-cover rounded-xl"
          src={data.thumbnail_url}
          alt={data.name}
        />
      </div>
      <div
        className={cn(
          focus ? 'opacity-100' : 'opacity-0',
          'transition absolute inset-0 top-0 left-0 bg-gradient-to-b from-black/20 via-black/70 to-black/90 z-30 flex'
        )}
      >
        {focus ? (
          <div className="absolute top-0 right-0 z-10 p-2">
            <button type="button" onClick={handleFavorite}>
              {isFavorite ? (
                <BsHeartFill className="h-6 w-6 text-primary-500" />
              ) : (
                <BsHeart className="h-6 w-6" />
              )}
            </button>
          </div>
        ) : null}
        <div className="m-auto text-xs text-white">
          {data.is_playable ? (
            <div className="text-xs font-light animate-pulse">
              Click to play
            </div>
          ) : (
            <div className="text-xs font-semibold italic">Coming Soon</div>
          )}
        </div>
        <div className="absolute bottom-0 px-3 pb-2">
          <div className="font-bold text-sm text-white leading-none">
            {data.name}
          </div>
        </div>
      </div>
    </GridWrapper>
  );
};

GridItem.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  onSelectGame: PropTypes.func.isRequired,
};

const ListItem = ({ data, onSelectGame }) => {
  const ref = React.useRef();
  const [focus, setFocus] = React.useState(false);
  useOnClickOutside(ref, () => setFocus(false));
  const [partner] = usePartner();
  const handleSelect = () => {
    if (focus && data.is_playable && !data?.is_undermaintenance) {
      onSelectGame(data);
      openGame(data.id, data.url, partner.id);
    }
    if (
      focus &&
      !data.is_playable &&
      (data.code === 'gb-hideandseek' ||
        data.code === 'gb-pitikbulag' ||
        data.code === 'gb-sipa') &&
      !data.is_undermaintenance
    ) {
      window.location.href = `/coming-soon?code=${data?.code}`;
    }
    setFocus(true);
  };
  const [isFavorite, { setFavorite, removeFavorite }] = useIsFavorite(data.id);
  const handleFavorite = () => {
    if (!isFavorite) setFavorite(data.id);
    if (isFavorite) removeFavorite(data.id);
  };

  return (
    <ListWrapper ref={ref} className="flex p-2 items-center">
      <button
        className={cn(
          'absolute inset-0 h-full w-full focus:outline-none z-10 block rounded-lg',
          'focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
        )}
        type="button"
        onClick={handleSelect}
      >
        <span className="sr-only">Select {data.name}</span>
      </button>
      <div className="flex-shrink-0 relative">
        {focus && !data.is_playable ? (
          <div className="bg-black/50 inset-0 h-full w-full absolute z-10 flex">
            <div className="m-auto text-white font-bold">Soon</div>
          </div>
        ) : null}
        <img
          className={cn(
            'h-14 w-24 rounded-lg z-0 object-cover',
            !data.is_playable ? 'filter grayscale' : ''
          )}
          src={data.thumbnail_url}
          alt={data.name}
        />
      </div>
      <div className="ml-3 pr-5 truncate">
        <div className="font-bold text-sm">
          <span>{data.name}</span>
        </div>
        <div className="font-light text-xs truncate">{data.genre_label}</div>
      </div>
      {focus ? (
        <div className="ml-auto mr-8">
          {data.is_playable ? (
            <div className="text-xs font-light">Click to play</div>
          ) : (
            <div className="text-xs font-semibold italic text-gray-500">
              {data.is_undermaintenance ? 'Under Maintenance' : 'Coming Soon'}
            </div>
          )}
        </div>
      ) : null}
      <div className="absolute top-0 right-0 z-10 p-2">
        <button type="button" onClick={handleFavorite}>
          {isFavorite ? (
            <BsHeartFill className="h-6 w-6 text-primary-500" />
          ) : (
            <BsHeart className="h-6 w-6" />
          )}
        </button>
      </div>
    </ListWrapper>
  );
};

ListItem.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  onSelectGame: PropTypes.func.isRequired,
};

function GameList({ isLoading, data, viewMode, renderEmpty }) {
  const [isGameLoading, setIsGameLoading] = React.useState(false);
  if (isLoading && viewMode === 'grid') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <GridWrapper className="animate-pulse">&nbsp;</GridWrapper>
        <GridWrapper className="animate-pulse">&nbsp;</GridWrapper>
        <GridWrapper className="animate-pulse">&nbsp;</GridWrapper>
        <GridWrapper className="animate-pulse">&nbsp;</GridWrapper>
      </div>
    );
  }
  if (isLoading && viewMode === 'list') {
    return (
      <div className="grid grid-cols-1 gap-3">
        <ListWrapper className="animate-pulse">&nbsp;</ListWrapper>
        <ListWrapper className="animate-pulse">&nbsp;</ListWrapper>
        <ListWrapper className="animate-pulse">&nbsp;</ListWrapper>
        <ListWrapper className="animate-pulse">&nbsp;</ListWrapper>
      </div>
    );
  }
  if (data.length < 1) {
    return (
      <div className="text-center text-sm text-gray-400 p-4">{renderEmpty}</div>
    );
  }
  if (viewMode === 'grid') {
    return (
      <>
        <div
          className={cn(
            'fixed top-0 left-0 inset-0 bg-gray-50 z-50 flex transition-opacity ease-out',
            isGameLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <div className="m-auto">Launching game, Please wait...</div>
        </div>
        <div className="grid grid-cols-2 gap-3 relative z-10">
          {data.map((item) => (
            <GridItem
              key={item.id}
              data={item}
              onSelectGame={() => setIsGameLoading(true)}
            />
          ))}
        </div>
      </>
    );
  }
  return (
    <>
      <div
        className={cn(
          'fixed top-0 left-0 inset-0 bg-gray-50 z-50 flex transition-opacity ease-out',
          isGameLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        <div className="m-auto">Launching game, Please wait...</div>
      </div>
      <div className="grid grid-cols-1 gap-3 relative z-10">
        {data.map((item) => (
          <ListItem
            key={item.id}
            data={item}
            onSelectGame={() => setIsGameLoading(true)}
          />
        ))}
      </div>
    </>
  );
}

GameList.defaultProps = {
  renderEmpty: 'There are no available games.',
};

GameList.propTypes = {
  renderEmpty: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.instanceOf(Array).isRequired,
  viewMode: PropTypes.string.isRequired,
};

export default GameList;
